import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, UseQueryOptions, QueryKey } from "react-query";
import { debounce, throttle, isEqual } from "lodash";

export interface SearchConfig {
  minCharacters?: number;
  debounceDelay?: number;
  throttleDelay?: number;
  enabled?: boolean;
  loadFirstPage?: boolean;
  logEnabled?: boolean;
  cancelOnUnMount?: boolean;
}

export interface UseSearchQueryOptions<
  TQueryFnData = unknown,
  TError = unknown,
  TData = unknown,
  TFilters extends Record<string, any> = Record<string, any>
> {
  filters?: TFilters;
  searchConfig?: SearchConfig;
  queryOptions?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, QueryKey>,
    "queryKey" | "queryFn" | "enabled"
  >;
}

/**
 * Advanced search hook with both debounce and throttle
 * Debounce: Waits for user to stop typing
 * Debounce.maxWait: Ensures updates happen at least every X ms for better UX
 */
export function useSearchQuery<
  TData = unknown,
  TQueryFnData = unknown,
  TFilters extends Record<string, any> = Record<string, any>,
  TError = unknown
>({
  queryKeyPrefix,
  searchTerm,
  filters,
  queryFn,
  options,
}: {
  queryKeyPrefix: string | string[];
  searchTerm: string;
  filters: TFilters;
  queryFn: (
    search: string,
    filters: TFilters,
    signal?: AbortSignal
  ) => Promise<TQueryFnData>;
  options?: UseSearchQueryOptions<TQueryFnData, TError, TData>;
}) {
  const { searchConfig = {}, queryOptions = {} } = options || {};

  const {
    minCharacters = 3,
    debounceDelay = 500,
    throttleDelay = 1000,
    loadFirstPage = false,
    enabled = true,
    logEnabled = false,
    cancelOnUnMount = false,
  } = searchConfig;

  // State for the processed search term
  const [processedSearch, setProcessedSearch] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const isValidProcesseSearch = (!!processedSearch || (loadFirstPage && processedSearch === "")) && processedSearch.length >= minCharacters;

  // Track previous filters to detect changes
  const prevFiltersRef = useRef(filters);
  const filtersChanged = !isEqual(prevFiltersRef.current, filters);

  useEffect(() => {
    prevFiltersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    
    if (logEnabled) console.log("loadFirstPage::", loadFirstPage);
    if (logEnabled) console.log("isValidProcesseSearch::", isValidProcesseSearch);
    if (logEnabled) console.log("processedSearch::", processedSearch);
  }, [isValidProcesseSearch, loadFirstPage, logEnabled, processedSearch]);

  // Debounced update - waits for user to stop typing
  // Create a stable debounced function with maxWait
  const debouncedUpdateRef = useRef(
    debounce(
      (value: string) => {
        if (logEnabled)
          console.log(
            "Debounced function executing:",
            value,
            new Date().toISOString()
          );
        setProcessedSearch(value);
        setIsTyping(false);
      },
      debounceDelay,
      {
        maxWait: throttleDelay,
        leading: false,
        trailing: true,
      }
    )
  );

  // Update the debounced function if delays change
  useEffect(() => {
    debouncedUpdateRef.current = debounce(
      (value: string) => {
        if (logEnabled)
          console.log(
            "Debounced function executing:",
            value,
            new Date().toISOString()
          );
        setProcessedSearch(value);
        setIsTyping(false);
      },
      debounceDelay,
      {
        maxWait: throttleDelay,
        leading: false,
        trailing: true,
      }
    );

    return () => {
      debouncedUpdateRef.current.cancel();
    };
  }, [debounceDelay, logEnabled, throttleDelay]);

  // Handle search term changes
  useEffect(() => {
    const trimmedSearch = searchTerm.trim();

    // Clear immediately if too short
    if (!trimmedSearch || trimmedSearch.length < minCharacters) {
      debouncedUpdateRef.current.cancel();
      setProcessedSearch("");
      setIsTyping(false);
      return;
    }

    if (logEnabled)
      console.log("Input changed:", trimmedSearch, new Date().toISOString());
    setIsTyping(true);
    debouncedUpdateRef.current(trimmedSearch);

    // Cleanup
    return () => {
      // Don't cancel on unmount to allow pending updates to complete
      if (cancelOnUnMount) debouncedUpdateRef.current.cancel();
    };
  }, [searchTerm, minCharacters, logEnabled, cancelOnUnMount]);

  // Handle filter changes
  useEffect(() => {
    if (filtersChanged && processedSearch) {
      // Force a new query with the same search term but new filters
      const currentSearch = processedSearch;
      setProcessedSearch("");
      setTimeout(() => setProcessedSearch(currentSearch), 0);
    }
  }, [filtersChanged, processedSearch]);

  // Build query key including filters
  const queryKey = useMemo(() => {
    const prefix = Array.isArray(queryKeyPrefix)
      ? queryKeyPrefix
      : [queryKeyPrefix];
    return [...prefix, processedSearch, filters] as QueryKey;
  }, [queryKeyPrefix, processedSearch, filters]);

  // Check if query should be enabled
  const isQueryEnabled =
    enabled && isValidProcesseSearch;
  // Execute query
  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      if (!isValidProcesseSearch) {
        return null as TQueryFnData;
      }
      return queryFn(processedSearch, filters, signal);
    },
    enabled: isQueryEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    ...queryOptions,
  });

  return {
    ...query,
    processedSearch,
    isTyping,
    isSearching: query.isFetching || isTyping,
    searchTerm,
    filters,
    isSearchValid: searchTerm.trim().length >= minCharacters,
  };
}
